import { memo, useCallback, useState } from 'react';
import {
  isSafeFetchUrl,
  isSafeHttpUrl,
  openExternalUrl,
} from '@/utils/url-utils';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import * as Sentry from '@sentry/react';
import { Download, ExternalLink, Eye, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  ArtifactNodeData,
  InputValue,
  NodeFile,
  NodeInputType,
} from '@/types/node-types';
import {
  downloadFileWithName,
  refreshFileUrls,
} from '@/services/workflow/get-file';
import { getCleanMarkdownData } from '@/pages/workflow/helper/markdown-utils';
import { useWorkflowPanelStore } from '@/stores/workflow-panel-store';
import { useOrgStore } from '@/stores/org-store';
import {
  OFFICE_EXTENSIONS,
  officeEmbedUrl,
} from '@/pages/workflow/component/display-components';
import {
  HANDLE_STYLING,
  HandleTypes,
  getFileIcon,
  getFileIconBackgroundColor,
} from '@/constants/node-styling';
import { FileIconTile } from '@/pages/workflow/component/file-icon-tile';
import { toSameOriginWorkflowUrl } from '@/utils/workflow-file-url';

const FILE_PORT_COLOR = HANDLE_STYLING[HandleTypes.PARAM_FILE].color;

const ARTIFACT_HANDLE_ID = 'artifact-target';

function getAppName(ext: string): string {
  switch (ext) {
    case 'pptx':
    case 'ppt':
      return 'Microsoft PowerPoint';
    case 'docx':
    case 'doc':
      return 'Microsoft Word';
    case 'xlsx':
    case 'xls':
    case 'csv':
      return 'Microsoft Excel';
    case 'pdf':
      return 'PDF document';
    case 'md':
      return 'Markdown document';
    case 'txt':
      return 'Plain text';
    default:
      return ext.toUpperCase() + ' file';
  }
}

// Mime types and extensions that the preview panel can render via text fetch.
const TEXT_FETCHABLE_EXTS = new Set(['md', 'txt']);
const TEXT_FETCHABLE_MIMES = new Set(['text/markdown', 'text/plain']);

function ArtifactNode({ data }: NodeProps) {
  const { file, fileDetail, content, contentType } =
    data as unknown as ArtifactNodeData;
  const { currentOrg } = useOrgStore();
  const showPreview = useWorkflowPanelStore((s) => s.showPreview);
  const hidePreview = useWorkflowPanelStore((s) => s.hidePreview);
  const [isDownloading, setIsDownloading] = useState(false);
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  const FileIcon = getFileIcon(file.name);
  const accent = getFileIconBackgroundColor(file.name);
  const isContentArtifact = content !== undefined;
  // True for real (S3-backed) markdown/text files where the content must be
  // fetched rather than read from the inline ``content`` field.
  const isFileFetchableText =
    !isContentArtifact &&
    (TEXT_FETCHABLE_EXTS.has(ext) ||
      TEXT_FETCHABLE_MIMES.has(file.mime_type ?? '')) &&
    !!(
      file.presigned_url ||
      (file as NodeFile & { download_url?: string }).download_url
    );

  const handleOpen = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();

      // Open a blank tab synchronously to preserve user activation so popup
      // blockers don't interfere with the tab opened after the async refresh.
      // Nullify opener immediately to prevent reverse tabnabbing.
      const newTab = window.open('', '_blank');
      if (newTab) newTab.opener = null;

      let fileToOpen = file;
      if (currentOrg?.orgid && file.s3_bucket && file.s3_key) {
        try {
          const [freshFile] = await refreshFileUrls(currentOrg.orgid, [file]);
          if (freshFile) fileToOpen = freshFile;
        } catch (refreshErr) {
          Sentry.captureException(refreshErr, {
            extra: { orgId: currentOrg.orgid, fileName: file.name },
          });
        }
      }

      const url = getArtifactFileUrl(fileToOpen);
      if (newTab) {
        if (url && isSafeHttpUrl(url)) {
          newTab.location.href = url;
        } else {
          newTab.close();
        }
      } else if (url) {
        openExternalUrl(url);
      }
    },
    [file, currentOrg?.orgid],
  );

  // Render the artifact's own content in the shared preview panel. Build the
  // result from the stored content so it works regardless of the parent node's
  // live state (a parent lookup is unreliable for fresh live runs).
  const handleOpenContent = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      showPreview({
        result: {
          display_modal:
            contentType === 'markdown'
              ? NodeInputType.MARKDOWN
              : NodeInputType.TEXT_OUTPUT,
          value: content,
        } as unknown as Record<string, InputValue>,
        isLoading: false,
        icon: 'FileText',
        displayName: file.name,
        nodeType: '',
      });
    },
    [showPreview, content, contentType, file.name],
  );

  // For real S3-backed markdown/text files: fetch the full content and show
  // it in the preview panel inline, instead of opening a new browser tab.
  // Uses the same same-origin proxy rewrite as FileRenderer so CORS is not
  // an issue on PR preview environments.
  const handleOpenFileContent = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();

      let fileToFetch = file;
      if (currentOrg?.orgid && file.s3_bucket && file.s3_key) {
        try {
          const [freshFile] = await refreshFileUrls(currentOrg.orgid, [file]);
          if (freshFile) fileToFetch = freshFile;
        } catch (refreshErr) {
          Sentry.captureException(refreshErr, {
            extra: { context: 'fetch-file-content', fileName: file.name },
          });
        }
      }

      const fileWithDownload = fileToFetch as NodeFile & {
        download_url?: string;
      };
      const rawUrl =
        fileWithDownload.presigned_url || fileWithDownload.download_url || '';
      const fetchUrl = toSameOriginWorkflowUrl(rawUrl);
      if (!fetchUrl) return;

      if (!isSafeFetchUrl(fetchUrl)) {
        // Deliberately returning here instead of throwing inside the try
        // below -- that catch's fallback opens fileToFetch's URL in a new
        // tab, which for this failure reason would just navigate to the
        // same unsafe host anyway.
        Sentry.captureException(
          new Error('Refusing to fetch an unsafe file URL'),
          { extra: { context: 'open-file-content', fileName: file.name } },
        );
        return;
      }

      const displayModal =
        ext === 'md' || fileToFetch.mime_type === 'text/markdown'
          ? NodeInputType.MARKDOWN
          : NodeInputType.TEXT_OUTPUT;

      // Show spinner in the panel while loading.
      showPreview({
        result: {
          display_modal: displayModal,
          value: null,
        } as unknown as Record<string, InputValue>,
        isLoading: true,
        icon: 'FileText',
        displayName: file.name,
        nodeType: '',
      });

      try {
        const res = await fetch(fetchUrl);
        if (!res.ok)
          throw new Error(`Failed to fetch file content: ${res.status}`);
        const text = await res.text();
        showPreview({
          result: {
            display_modal: displayModal,
            value: text,
          } as unknown as Record<string, InputValue>,
          isLoading: false,
          icon: 'FileText',
          displayName: file.name,
          nodeType: '',
        });
      } catch (fetchErr) {
        Sentry.captureException(fetchErr, {
          extra: { context: 'open-file-content', fileName: file.name },
        });
        // Fall back: open the file in a new tab so the user isn't left with a
        // stuck spinner and still has access to the content. Close the panel
        // too -- otherwise it stays on the isLoading:true preview shown above
        // forever, since nothing else clears it. Guard on identity in case
        // the user has since opened a different node's preview.
        const { previewData } = useWorkflowPanelStore.getState();
        if (previewData?.displayName === file.name && previewData.isLoading) {
          hidePreview();
        }
        const fallbackUrl = getArtifactFileUrl(fileToFetch);
        if (fallbackUrl) openExternalUrl(fallbackUrl);
      }
    },
    [file, currentOrg?.orgid, showPreview, hidePreview, ext],
  );

  const handleDownload = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isDownloading) return;

      setIsDownloading(true);
      try {
        if (isContentArtifact) {
          const body =
            contentType === 'markdown'
              ? getCleanMarkdownData(content ?? '')
              : (content ?? '');
          const mime =
            contentType === 'markdown' ? 'text/markdown' : 'text/plain';
          const blob = new Blob([body], { type: mime });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = file.name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          return;
        }

        let fileToDownload = file;

        if (currentOrg?.orgid && file.s3_bucket && file.s3_key) {
          try {
            const [freshFile] = await refreshFileUrls(currentOrg.orgid, [file]);
            if (freshFile) fileToDownload = freshFile;
          } catch (refreshErr) {
            console.error(
              'Failed to refresh file URL for download:',
              refreshErr,
            );
          }
        }

        const downloadUrl =
          fileToDownload.download_url || fileToDownload.presigned_url;
        await downloadFileWithName(downloadUrl, file.name);
      } catch (err) {
        console.error('Failed to download file:', err);
      } finally {
        setIsDownloading(false);
      }
    },
    [
      file,
      currentOrg?.orgid,
      isDownloading,
      isContentArtifact,
      content,
      contentType,
    ],
  );

  return (
    <div className='w-[280px] overflow-hidden rounded-[10px] border border-border bg-card shadow-sm'>
      <Handle
        type='target'
        position={Position.Left}
        id={ARTIFACT_HANDLE_ID}
        style={{
          width: 6,
          height: 44,
          background: FILE_PORT_COLOR,
          left: -6,
          top: '50%',
          transform: 'translateY(-50%)',
          borderRadius: '6px 0 0 6px',
          boxShadow: '0 1px 4px rgba(38, 132, 255, 0.08)',
          border: 'none',
        }}
      />

      <div style={{ height: 4, background: accent }} />

      <div className='flex items-center gap-2.5 px-3.5 pt-2.5'>
        <FileIconTile icon={FileIcon} backgroundColor={accent} size='small' />
        <div className='min-w-0 flex-1'>
          <div
            className='text-[10px] font-semibold uppercase leading-tight tracking-wide'
            style={{ color: accent }}
          >
            {getAppName(ext)}
          </div>
          <div className='mt-0.5 truncate text-sm font-semibold leading-tight text-foreground'>
            {file.name}
          </div>
        </div>
      </div>

      {fileDetail && (
        <div className='px-3.5 pt-2 text-[11px] text-muted-foreground'>
          {fileDetail}
        </div>
      )}

      <div className='mt-2.5 grid grid-cols-2 gap-2 border-t border-border px-2 py-2'>
        <Button
          variant='outline'
          size='sm'
          onClick={
            isContentArtifact
              ? handleOpenContent
              : isFileFetchableText
                ? handleOpenFileContent
                : handleOpen
          }
          className='gap-1.5 text-[11.5px]'
        >
          {isContentArtifact || isFileFetchableText ? (
            <Eye className='size-3 shrink-0' />
          ) : (
            <ExternalLink className='size-3 shrink-0' />
          )}
          Open
        </Button>
        <Button
          variant='outline'
          size='sm'
          onClick={handleDownload}
          disabled={isDownloading}
          className='gap-1.5 text-[11.5px]'
        >
          {isDownloading ? (
            <Loader2 className='size-3 shrink-0 animate-spin' />
          ) : (
            <Download className='size-3 shrink-0' />
          )}
          Download
        </Button>
      </div>
    </div>
  );
}

function getArtifactFileUrl(file: NodeFile): string | null {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (OFFICE_EXTENSIONS.includes(ext)) {
    const previewUrl = file.proxy_url || file.presigned_url;
    return previewUrl ? officeEmbedUrl(previewUrl) : null;
  }
  return file.presigned_url ?? null;
}

export { ARTIFACT_HANDLE_ID };
export default memo(ArtifactNode);
